import PropTypes from 'prop-types'
import { createMarkup, stripTitleHTML } from '../../../helpers/markup'
import { Arrow } from '../Icons'
import { ChevronDownIcon } from '@heroicons/react/24/solid'

const JumpMenuItem = ({ item, activeSection, handleNavigationClick, variant }) => {
  const isActive = activeSection === item.id

  if (variant === 'desktop') {
    return (
      <button
        key={item.id}
        id={`${item.id}-trigger`}
        type="button"
        onClick={(evt) => handleNavigationClick(evt, item.id)}
        className={`jump-menu-item group text-left w-full font-sans text-lg leading-7 transition-colors ${
          isActive ? 'text-nds-dark-blue font-medium' : 'text-nds-90-black font-normal hover:bg-grey-subtle'
        }`}
      >
        <span
          className={`flex items-center px-6 py-3.5 border-l-[5px] transition-all duration-300 ${
            isActive ? 'border-l-nds-dark-blue' : 'border-l-transparent'
          }`}
        >
          <span className="jump-menu-item__label group-hover:underline decoration-current group-hover:decoration-current underline-offset-4">
            {stripTitleHTML(createMarkup(item.label))}
          </span>
        </span>
      </button>
    )
  }

  return (
    <button
      key={item.id}
      id={`${item.id}-trigger`}
      type="button"
      onClick={(evt) => handleNavigationClick(evt, item.id)}
      className={`jump-menu-item group text-left w-full text-lg border-t border-primary-border transition-colors font-medium ${
        isActive ? 'bg-grey-subtle text-secondary' : 'text-primary hover:bg-grey-subtle'
      }`}
    >
      <span
        className={`flex gap-2 items-center justify-between px-6 py-3.5 border-l-4 transition-all duration-300 ${isActive ? 'border-l-secondary' : 'border-l-transparent'}`}
      >
        <span className="jump-menu-item__label group-hover:underline decoration-current group-hover:decoration-current underline-offset-4">
          {stripTitleHTML(createMarkup(item.label))}
        </span>
        <span
          className={`shrink-0 ${isActive ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        >
          <Arrow />
        </span>
      </span>
    </button>
  )
}

const JumpNav = ({ navigationItems, activeSection, handleNavigationClick, variant }) => {
  return (
    <nav>
      {navigationItems.map((item) => (
        <JumpMenuItem
          key={item.id}
          item={item}
          activeSection={activeSection}
          handleNavigationClick={handleNavigationClick}
          variant={variant}
        />
      ))}
    </nav>
  )
}

const handleDeviceNavToggle = evt => {
  const trigger = document.getElementById('jump-menu-device-nav-trigger')
  const nav = document.getElementById('jump-menu-device-nav')
  if (trigger.getAttribute('aria-expanded') === 'false') {
    trigger.setAttribute('aria-expanded', 'true')
    nav.setAttribute('aria-hidden', 'false')
  } else {
    trigger.setAttribute('aria-expanded', 'false')
    nav.setAttribute('aria-hidden', 'true')
  }
}

const JumpMenuDevice = ({ navigationItems, sidebarTitle, activeSection, handleNavigationClick }) => {
  return (
    <>
      {navigationItems.length > 0 && (
        <div className="col-span-1 w-full">
          <div className="max-h-[calc(100vh-8rem)] overflow-y-auto relative w-screen left-1/2 -ml-[50vw]">
            <button id="jump-menu-device-nav-trigger" aria-controls="jump-menu-device-nav" aria-expanded="false" className="flex items-center justify-between w-full text-left bg-nds-blue text-white rounded-none shadow-sm px-4 py-3 transition-colors group/jumpTrigger" onClick={(evt) => {
              handleDeviceNavToggle(evt)
            }}>
              <span className="font-sans text-base font-normal leading-6 text-white">{sidebarTitle}</span>
              <ChevronDownIcon className="w-6 h-6 text-white group-aria-expanded/jumpTrigger:rotate-180 transition-transform duration-300" />
            </button>
          </div>
          <div id="jump-menu-device-nav" aria-hidden="true" className="aria-hidden:max-h-0 overflow-hidden max-h-[calc(100vh-8rem)] transition-all duration-300">
            <div className="py-2 px-3">
              <JumpNav
                navigationItems={navigationItems}
                activeSection={activeSection}
                handleNavigationClick={handleNavigationClick}
                variant="desktop"
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const JumpMenu = ({ navigationItems, sidebarTitle, activeSection, handleNavigationClick }) => {
  return (
    <>
      {navigationItems.length > 0 && (
        <div className="lg:col-span-1">
          <div className="sticky top-24 -scale-x-100 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div className="-scale-x-100 pl-2">
              <h3 className="font-sans text-4xl font-light leading-10 mb-4 text-nds-blue">{sidebarTitle}</h3>
              <JumpNav
                navigationItems={navigationItems}
                sidebarTitle={sidebarTitle}
                activeSection={activeSection}
                handleNavigationClick={handleNavigationClick}
                variant="desktop"
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

JumpMenuItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  activeSection: PropTypes.string,
  handleNavigationClick: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['desktop']),
}

JumpNav.propTypes = {
  navigationItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  activeSection: PropTypes.string,
  handleNavigationClick: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['desktop']),
}

JumpMenuDevice.propTypes = {
  navigationItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  sidebarTitle: PropTypes.string,
  activeSection: PropTypes.string,
  handleNavigationClick: PropTypes.func.isRequired,
}

JumpMenu.propTypes = {
  navigationItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  sidebarTitle: PropTypes.string,
  activeSection: PropTypes.string,
  handleNavigationClick: PropTypes.func.isRequired,
}

export { JumpMenu, JumpMenuDevice, JumpNav }
